import React from 'react';
import { useLang } from '../../../contexts/LangContext';

export interface TabConfig {
  id: string;
  label: string;
}

export interface ActionButton {
  label: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'success';
  icon?: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

interface IncomeExpensePageShellProps {
  pageTitle: string;
  pageCode: string;
  tabs: TabConfig[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  actions?: ActionButton[];
  badge?: number;
  children: React.ReactNode;
}

const actionVariantClasses: Record<NonNullable<ActionButton['variant']>, string> = {
  primary: 'bg-orange-500 hover:bg-orange-600 text-white',
  secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-700',
  outline: 'border border-pink-300 text-pink-600 hover:bg-pink-50',
  danger: 'bg-red-50 border border-red-200 text-red-600 hover:bg-red-100',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
};

export const IncomeExpensePageShell: React.FC<IncomeExpensePageShellProps> = ({
  pageTitle,
  pageCode,
  tabs,
  activeTab,
  onTabChange,
  actions = [],
  badge,
  children,
}) => {
  const { t } = useLang();

  return (
    <div className="max-w-[1400px] mx-auto p-6">
      <nav className="flex mb-4 text-sm text-gray-500">
        <span>{t('INCOME_EXPENSE_MANAGEMENT') || 'QUẢN LÝ THU CHI'}</span>
        <span className="mx-2">/</span>
        <span className="text-cyan-600 font-medium">{t(pageCode) || pageTitle}</span>
      </nav>

      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`px-5 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-cyan-600 text-cyan-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-gray-900 uppercase">{pageTitle}</h1>
          {badge !== undefined && (
            <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-cyan-100 text-cyan-700 text-sm font-semibold">
              {badge}
            </span>
          )}
        </div>
        {actions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                onClick={action.onClick}
                disabled={action.disabled ?? !action.onClick}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  actionVariantClasses[action.variant ?? 'secondary']
                }`}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {children}
    </div>
  );
};
